import './PlacesInput.css';

import { Flex } from '../../flayr-components/Flex/Flex';
import React from 'react';

import { ComponentsContext } from '../../contexts/componentsContext';
import MapboxGeocoder from '../MapboxGeocoder/MapboxGeocoder';
import { PLACES_INPUT_AVAILABLE_COUNTRIES } from '../../config';
import classNames from 'classnames';
import {
  convertMapboxLocationToLocation,
  getClearedLocation,
} from '../../utils/projectSpecificUtils';
import { abbreviateAustralianStatesInLabel } from '../../utils/abbreviateAustralianStatesInLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';

/** Lets MapboxGeocoder use a stable `inputComponent` identity; styling/clear UI update via context. */
type TPlacesInputGeocoderUi = {
  inputClassName: string;
  inputStyle?: React.CSSProperties;
  placeholder?: string;
  autoFocus?: boolean;
  inputId?: string;
  forwardedRef: React.Ref<HTMLInputElement>;
  hideClear?: boolean;
  clearBtnClassName: string;
  clearBtnContainerClassname?: string;
  onClearPress: (e: React.MouseEvent) => void;
};

const PlacesInputGeocoderUiContext = React.createContext<TPlacesInputGeocoderUi | null>(
  null
);

const PlacesInputCheckoutSuggestionsContext = React.createContext(false);

/** Stable row for inline results — map pin + label (MapboxGeocoder `itemComponent`). */
function PlacesInputInlineGeocoderItem(
  props: React.HTMLAttributes<HTMLDivElement> & { item?: unknown }
) {
  const { className, onClick, children, item: _item, ...rest } = props;
  const checkout = React.useContext(PlacesInputCheckoutSuggestionsContext);
  return (
    <div
      {...rest}
      className={classNames(
        className,
        checkout ? 'items-center gap-3' : 'items-start gap-3'
      )}
      onClick={onClick}
    >
      <span
        className={classNames(
          'flex h-9 w-9 shrink-0 items-center justify-center text-gray-500',
          checkout ? 'rounded-lg bg-gray-100' : 'rounded-md bg-gray-100'
        )}
        aria-hidden
      >
        <FontAwesomeIcon icon={faMapMarkerAlt} className="h-4 w-4" />
      </span>
      <span
        className={classNames(
          'min-w-0 flex-1 text-left leading-snug',
          checkout
            ? 'self-center font-geist text-sm font-normal text-gray-900'
            : 'self-center leading-tight'
        )}
      >
        {children}
      </span>
    </div>
  );
}

function PlacesInputGeocoderInput(
  props: React.InputHTMLAttributes<HTMLInputElement>
) {
  const ctx = React.useContext(PlacesInputGeocoderUiContext);
  if (!ctx) {
    return <input {...props} />;
  }
  const showClear = !!props.value && !ctx.hideClear;
  return (
    <Flex y="stretch" className="relative">
      <input
        {...props}
        ref={ctx.forwardedRef}
        className={ctx.inputClassName}
        style={ctx.inputStyle}
        placeholder={ctx.placeholder}
        autoFocus={ctx.autoFocus}
        id={ctx.inputId}
        autoComplete="off"
      />
      <div
        className={classNames(
          'absolute top-0 right-0 flex items-center h-full pr-1',
          ctx.clearBtnContainerClassname
        )}
      >
        {showClear && (
          <button
            className={classNames(
              `h-8 w-8 focus:outline-none cursor-pointer`,
              ctx.clearBtnClassName,
              {
                'bg-gray-200 rounded-full shadow-sm': !ctx.clearBtnClassName,
              }
            )}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={ctx.onClearPress}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
    </Flex>
  );
}

interface IPlacesInputProps {
  index?: number;
  placeholder?: string;
  value?: string;
  hideClear?: boolean;
  optionsZIndex?: number;
  autoFocus?: boolean;
  label?: string;
  id?: string;
  optionsContainerClassName?: string;
  optionsContainerStyle?: React.CSSProperties;
  labelClassName?: string;
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  validation?: any[];
  fixedOptions?: boolean;
  /** When true, suggestion list sits in normal document flow under the input (no overlay). */
  inlineResults?: boolean;
  /**
   * When true with `inlineResults`, applies instant-book checkout styling (pill-adjacent list,
   * AU state abbreviations in labels) — used by AddressStep.
   */
  checkoutSuggestions?: boolean;
  /** Override Mapbox `formatItem` for suggestion rows (defaults to AU state abbreviations when `checkoutSuggestions`). */
  formatItem?: (item: any) => string;
  /**
   * When true (default), suggestions hide on input blur. When false, they stay visible.
   * If omitted, defaults to !inlineResults (inline lists keep suggestions after blur).
   */
  hideSuggestionsOnBlur?: boolean;
  clearBtnClassName?: string;
  clearBtnContainerClassname?: string;
  formatLocation?: boolean;
  countries?: string[];
  proximity?: number[];
  /**
   * Mapbox forward geocode `types` (comma-separated), e.g. `address` for street addresses only.
   * See Mapbox Geocoding API.
   */
  mapboxTypes?: string;
  filterResults?: (e?: any) => void;
  /**
   * Maps the user’s input to the query string sent to Mapbox forward geocode only.
   * Use to bias results (e.g. append suburb/region) without changing the visible field.
   */
  forwardGeocodeQueryTransform?: (userQuery: string) => string;
  onFocus?: (e: any) => void;
  onSelect?: (e: any) => void;
  onClear?: (e?: any) => void;
  onChange?: (e: any) => void;
  onBlur?: (e: any) => void;
  onOptionsUpdate?: (e: any) => void;
}

const PlacesInput = React.forwardRef<HTMLInputElement, IPlacesInputProps>(
  (
    {
      onSelect,
      index,
      placeholder,
      value,
      hideClear,
      optionsZIndex = 40,
      autoFocus,
      onClear,
      label,
      labelClassName,
      validation,
      inputClassName = 'flex-grow px-4 py-2 text-lg bg-white bg-opacity-50 border border-gray-200 rounded focus:outline-none focus:border focus:border-gray-500 focus:bg-opacity-100',
      inputStyle,
      fixedOptions = true,
      inlineResults = false,
      checkoutSuggestions = false,
      formatItem: formatItemProp,
      hideSuggestionsOnBlur: hideSuggestionsOnBlurProp,
      clearBtnClassName = '',
      clearBtnContainerClassname,
      optionsContainerClassName,
      optionsContainerStyle,
      formatLocation,
      id,
      onFocus,
      onChange,
      countries = [],
      mapboxTypes,
      filterResults,
      forwardGeocodeQueryTransform,
      proximity,
      onOptionsUpdate,
      onBlur,
    },
    ref
  ) => {
    const [viewport, setViewport] = React.useState<any>({});

    const optionsRef = React.useRef<HTMLInputElement>(null);
    const mapboxGeocoderRef = React.useRef<InstanceType<typeof MapboxGeocoder> | null>(
      null
    );
    const context = React.useContext(ComponentsContext);

    const onSelected = (viewport, item) => {
      setViewport(viewport);
      if (formatLocation) {
        item = convertMapboxLocationToLocation(item);
      }
      onSelect && onSelect({ ...item, index });
    };

    const handleClear = React.useCallback(
      async (e: React.MouseEvent) => {
        mapboxGeocoderRef.current?.clearInput();
        if (formatLocation) {
          onOptionsUpdate?.([]);
          onSelect?.(getClearedLocation());
        } else {
          onClear?.(index);
        }
      },
      [formatLocation, index, onClear, onOptionsUpdate, onSelect]
    );

    const queryParams = React.useMemo(() => {
      const qp: any = {};

      qp.country =
        (countries?.length && countries.join(',')) ||
        PLACES_INPUT_AVAILABLE_COUNTRIES;

      if (proximity && proximity.length === 2) {
        qp.proximity = {
          longitude: proximity[0],
          latitude: proximity[1],
        };
      }

      if (mapboxTypes && String(mapboxTypes).trim()) {
        qp.types = String(mapboxTypes).trim();
      }

      return qp;
    }, [countries, PLACES_INPUT_AVAILABLE_COUNTRIES, proximity, mapboxTypes]);

    const geocoderUi = React.useMemo(
      (): TPlacesInputGeocoderUi => ({
        inputClassName,
        inputStyle,
        placeholder,
        autoFocus,
        inputId: id,
        forwardedRef: ref as React.Ref<HTMLInputElement>,
        hideClear,
        clearBtnClassName,
        clearBtnContainerClassname,
        onClearPress: handleClear,
      }),
      [
        inputClassName,
        inputStyle,
        placeholder,
        autoFocus,
        id,
        ref,
        hideClear,
        clearBtnClassName,
        clearBtnContainerClassname,
        handleClear,
      ]
    );

    if (!context.mapbox?.accessToken) {
      return null;
    }

    function getDimensions() {
      if (optionsRef.current) {
        const el = optionsRef.current;
        if (el) {
          const bb = el.getBoundingClientRect();
          return {
            '--left': `${bb.left || 0}px`,
            '--top': `${bb.top || 0}px`,
            '--z-index': optionsZIndex,
            '--width': `${bb.width || 0}px`,
          };
        }
      }
    }

    function handleOptionsUpdate(options) {
      if (formatLocation) {
        options = options.map((item) => convertMapboxLocationToLocation(item));
      }
      onOptionsUpdate?.(options);
    }

    const hideSuggestionsOnBlur =
      hideSuggestionsOnBlurProp !== undefined
        ? hideSuggestionsOnBlurProp
        : !inlineResults;

    const resolvedFormatItem =
      formatItemProp ??
      (checkoutSuggestions
        ? (item: any) =>
            abbreviateAustralianStatesInLabel(String(item?.place_name ?? ''))
        : undefined);

    return (
      <div
        className={classNames('relative PlacesInput', {
          'PlacesInput--inlineResults': inlineResults,
          'PlacesInput--checkout': checkoutSuggestions && inlineResults,
        })}
        style={getDimensions() as React.CSSProperties}
      >
        <label className={labelClassName}>{label}</label>

        <PlacesInputGeocoderUiContext.Provider value={geocoderUi}>
          <PlacesInputCheckoutSuggestionsContext.Provider value={checkoutSuggestions}>
            <MapboxGeocoder
              ref={mapboxGeocoderRef}
              mapboxApiAccessToken={context.mapbox.accessToken}
              onSelected={onSelected}
              viewport={viewport}
              updateInputOnSelect
              hideOnSelect
              value={value}
              fixedOptions={fixedOptions}
              queryParams={queryParams}
              onChange={onChange}
              optionsContainerClassName={classNames(
                checkoutSuggestions && '!shadow-none !translate-x-0',
                optionsContainerClassName
              )}
              optionsContainerStyle={optionsContainerStyle}
              option
              onFocus={onFocus}
              onBlur={onBlur}
              filterResults={filterResults}
              forwardGeocodeQueryTransform={forwardGeocodeQueryTransform}
              onClear={handleClear}
              onOptionsUpdate={handleOptionsUpdate}
              hideSuggestionsOnBlur={hideSuggestionsOnBlur}
              formatItem={resolvedFormatItem}
              inputComponent={PlacesInputGeocoderInput}
              itemComponent={inlineResults ? PlacesInputInlineGeocoderItem : undefined}
            />
          </PlacesInputCheckoutSuggestionsContext.Provider>
        </PlacesInputGeocoderUiContext.Provider>
        <div ref={optionsRef} className="w-full" />
      </div>
    );
  }
);
export default PlacesInput;
